import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useParams } from 'react-router-dom'
import {
  Copy, Hand, Link2, Mic, MicOff, PhoneOff, Play, Square, Video, VideoOff,
} from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import {
  endMeeting, getLobby, getMeeting, getMeetingNote, issueMeetingLink, joinMeeting,
  listMeetingMessages, listParticipants, postMeetingMessage, raiseHand, saveMeetingNote,
  startMeeting,
} from './api'
import { LobbyPanel } from './LobbyPanel'
import { ArchivePanel } from './ArchivePanel'
import { ChatPanel, NotesPanel } from './panels'
import { AnalysisPanel } from './AnalysisPanel'
import { TranscriptPanel } from './TranscriptPanel'
import { VideoGrid } from './VideoGrid'
import { connect, getSnapshot, leave, subscribe, toggleCamera, toggleMic } from './room'
import { meetingStatusLabel, whenLabel } from './vocab'
import type { MeetingParticipant } from './types'

/** The lobby is the only thing worth polling quickly: somebody is standing at a door. */
const LOBBY_POLL_MS = 4000
const ROSTER_POLL_MS = 10000

/**
 * One meeting: the room on the left, the door on the right.
 *
 * There is no push channel anywhere in this product, so both panels poll. The lobby polls
 * faster than the roster because the cost of being slow is different: a late roster shows a
 * name for a few seconds longer, a late lobby leaves a person staring at "waiting to be let
 * in" while the chair believes nobody is there.
 */
export function MeetingRoomPage() {
  const { meetingId = '' } = useParams()
  const canRead = useCan('meeting:read')
  const canJoin = useCan('meeting:join')
  const [tick, setTick] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [waitingToBeAdmitted, setWaitingToBeAdmitted] = useState(false)
  // Learned from the grant, which is the only place the server tells a browser which row in
  // the roster is its own. Without it this page could not tell whose hand is up.
  const [mine, setMine] = useState<string | null>(null)

  const media = useSyncExternalStore(subscribe, getSnapshot)
  const { data: meeting, error: loadError, loading } = useLoad(
    () => getMeeting(meetingId), [meetingId, tick])
  const refresh = useCallback(() => setTick((t) => t + 1), [])

  const isHost = Boolean(meeting?.callerIsHost)

  // ── the door, polled only by whoever may open it ──────────────────────────
  const [waiting, setWaiting] = useState<MeetingParticipant[]>([])
  const [roster, setRoster] = useState<MeetingParticipant[]>([])

  useEffect(() => {
    if (!isHost || !meetingId) return
    let live = true
    const pull = () => {
      getLobby(meetingId).then((lobby) => { if (live) setWaiting(lobby.waiting) }).catch(() => {})
    }
    pull()
    const t = setInterval(pull, LOBBY_POLL_MS)
    return () => { live = false; clearInterval(t) }
  }, [isHost, meetingId, tick])

  useEffect(() => {
    if (!canRead || !meetingId) return
    let live = true
    const pull = () => {
      listParticipants(meetingId).then((people) => { if (live) setRoster(people) }).catch(() => {})
    }
    pull()
    const t = setInterval(pull, ROSTER_POLL_MS)
    return () => { live = false; clearInterval(t) }
  }, [canRead, meetingId, tick])

  // ── taking a seat ─────────────────────────────────────────────────────────
  const take = useCallback(async () => {
    setError(null)
    try {
      const outcome = await joinMeeting(meetingId)
      if (!outcome.grant) {
        // The lobby is on and nobody has admitted us yet. Poll our own join rather than
        // inventing a second endpoint: the answer carries the grant the moment it exists.
        setWaitingToBeAdmitted(true)
        return
      }
      setWaitingToBeAdmitted(false)
      setMine(outcome.grant.participantId)
      await connect(outcome.grant, 'You')
    } catch (e) {
      setError(explain(e))
    }
  }, [meetingId])

  useEffect(() => {
    if (!waitingToBeAdmitted) return
    const t = setInterval(() => { void take() }, LOBBY_POLL_MS)
    return () => clearInterval(t)
  }, [waitingToBeAdmitted, take])

  // Leaving the page leaves the meeting. The room is a module singleton precisely so a panel
  // unmounting cannot do this by accident — but navigating away is not an accident.
  useEffect(() => () => { void leave() }, [])

  if (!canRead) return <Forbidden what="meetings" />
  if (loading && !meeting) return <LoadingBlock label="Opening the meeting…" />
  if (loadError || !meeting) return <ErrorStrip message={loadError ?? 'No such meeting'} />

  const inRoom = roster.filter((p) => p.inRoom)
  const connected = media.phase === 'connected'
  // Oldest hand first. The roster arrives in join order, which is not the order people asked
  // in, and the queue is the only reason the server stores a timestamp instead of a flag.
  const hands = roster
    .filter((p) => p.handRaisedAt && p.admissionCode === 'ADMITTED')
    .sort((a, b) => (a.handRaisedAt ?? '').localeCompare(b.handRaisedAt ?? ''))
  const myHandIsUp = Boolean(roster.find((p) => p.id === mine)?.handRaisedAt)

  return (
    <div className="space-y-4">
      <SectionHeader
        title={meeting.title}
        description={`${whenLabel(meeting.scheduledAt)} · ${meetingStatusLabel(meeting.statusCode)}`}
        right={(
          <>
            {isHost && meeting.statusCode === 'SCHEDULED' && (
              <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
                onClick={() => startMeeting(meetingId).then(refresh).catch((e) => setError(explain(e)))}>
                <Play className="size-4" /> Start
              </button>
            )}
            {isHost && meeting.statusCode === 'LIVE' && (
              <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm text-destructive"
                onClick={() => {
                  void leave()
                  endMeeting(meetingId).then(refresh).catch((e) => setError(explain(e)))
                }}>
                <Square className="size-4" /> End for everyone
              </button>
            )}
            {isHost && <LinkButton meetingId={meetingId} />}
          </>
        )}
      />

      <ErrorStrip message={error ?? media.error} />

      {/*
         Everybody in the room is told, in the place they are already looking. A recording
         indicator buried in a tab is a recording nobody knows about.
      */}
      {/*
          `recordingEnabled` is a SETTING ON THE MEETING, not a live state — it stays true
          after the meeting ends. Rendering the banner on that alone meant a finished
          meeting still showed a pulsing red "This meeting is being recorded", which is a
          false statement about surveillance. People accept being recorded on the strength
          of this indicator; an indicator that lies in either direction is worse than none,
          so it is now tied to the meeting actually being LIVE.
      */}
      {meeting.recordingEnabled && meeting.statusCode === 'LIVE' && (
        <p className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2
                      text-erp-sm text-destructive">
          <span className="size-2 animate-pulse rounded-full bg-destructive" />
          This meeting is being recorded.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Panel>
          {connected ? (
            <>
              <VideoGrid peers={media.peers} />
              <div className="mt-3 flex items-center justify-center gap-2">
                <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm"
                  onClick={() => void toggleMic()}>
                  {media.micOn ? <Mic className="size-4" /> : <MicOff className="size-4 text-destructive" />}
                  {media.micOn ? 'Mute' : 'Unmute'}
                </button>
                <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm"
                  onClick={() => void toggleCamera()}>
                  {media.cameraOn ? <Video className="size-4" /> : <VideoOff className="size-4 text-destructive" />}
                  {media.cameraOn ? 'Stop video' : 'Start video'}
                </button>
                <button type="button"
                  className={`erp-btn h-9 text-erp-sm ${
                    myHandIsUp ? 'erp-btn-primary' : 'erp-btn-ghost'}`}
                  onClick={() => {
                    // The answer is the roster, so the button's own state comes back from the
                    // server rather than being guessed locally and corrected a poll later.
                    raiseHand(meetingId, !myHandIsUp).then(setRoster)
                      .catch((e) => setError(explain(e)))
                  }}>
                  <Hand className="size-4" /> {myHandIsUp ? 'Lower hand' : 'Raise hand'}
                </button>
                <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm text-destructive"
                  onClick={() => void leave()}>
                  <PhoneOff className="size-4" /> Leave
                </button>
              </div>
              <HandQueue hands={hands} mine={mine} />
            </>
          ) : (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              {media.phase === 'connecting' ? (
                <LoadingBlock label="Connecting…" />
              ) : waitingToBeAdmitted ? (
                <>
                  <p className="font-medium">Waiting to be let in</p>
                  <p className="text-erp-sm text-muted-foreground max-w-sm">
                    The chair has been told you are here. This page lets you in by itself the
                    moment they admit you.
                  </p>
                </>
              ) : meeting.statusCode === 'ENDED' || meeting.statusCode === 'CANCELLED' ? (
                <Note tone="info">This meeting is over.</Note>
              ) : (
                <>
                  <p className="text-erp-sm text-muted-foreground max-w-sm">
                    Your camera and microphone are not opened until you join.
                  </p>
                  <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
                    disabled={!canJoin} onClick={() => void take()}>
                    <Video className="size-4" /> Join the meeting
                  </button>
                  {!canJoin && (
                    <p className="text-erp-xs text-muted-foreground">
                      You do not hold the permission to take a seat in meetings.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </Panel>

        <SidePanel meetingId={meetingId} isHost={isHost} waiting={waiting} inRoom={inRoom}
          handCount={hands.length} recordingEnabled={meeting.recordingEnabled}
          live={meeting.statusCode === 'LIVE'} onChanged={refresh} />
      </div>
    </div>
  )
}

/**
 * Who has asked to speak, in the order they asked.
 *
 * <p>Shown under the video to everybody and not only the chair, on purpose: a participant
 * who can see three hands ahead of theirs waits instead of interrupting, and somebody whose
 * hand is up needs to see that it is actually up. The chair's own copy of the same queue is
 * on the Door tab, beside the controls for acting on it.</p>
 *
 * Nothing renders when nobody has a hand up — an empty "Raised hands" row below every
 * meeting is a line of furniture.
 */
function HandQueue({ hands, mine }: { hands: MeetingParticipant[]; mine: string | null }) {
  if (hands.length === 0) return null
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2">
      <Hand className="size-4 shrink-0 text-amber-600" />
      <span className="text-erp-xs font-medium text-muted-foreground">
        {hands.length === 1 ? 'Asking to speak' : `${hands.length} asking to speak`}
      </span>
      {hands.map((person, i) => (
        <span key={person.id}
          className="inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5
                     text-erp-xs font-medium">
          <span className="text-muted-foreground">{i + 1}.</span>
          {person.id === mine ? 'You' : person.name ?? 'Someone'}
        </span>
      ))}
    </div>
  )
}

/**
 * Everything about the meeting that is not the picture.
 *
 * The door is first and only for a chair, and it carries the count — a tab that says "3" is
 * what makes somebody look at it. The three unbuilt tabs are shown rather than hidden: a
 * missing tab reads as a product that cannot do the thing.
 */
function SidePanel({
  meetingId, isHost, waiting, inRoom, handCount, recordingEnabled, live, onChanged,
}: {
  meetingId: string
  isHost: boolean
  waiting: MeetingParticipant[]
  inRoom: MeetingParticipant[]
  handCount: number
  recordingEnabled: boolean
  /** The meeting is LIVE. Recording copy is written in the present tense and needs it. */
  live: boolean
  onChanged: () => void
}) {
  type Tab = 'door' | 'chat' | 'notes' | 'archive' | 'transcript' | 'analysis'
  const [tab, setTab] = useState<Tab>(isHost ? 'door' : 'chat')

  const tabs: { id: Tab; label: string; badge?: number; hidden?: boolean }[] = [
    { id: 'door', label: 'Door', badge: waiting.length + handCount, hidden: !isHost },
    { id: 'chat', label: 'Chat' },
    { id: 'notes', label: 'Notes' },
    { id: 'archive', label: 'Archive' },
    { id: 'transcript', label: 'Transcript' },
    { id: 'analysis', label: 'Analysis' },
  ]

  const chatIo = useMemo(() => ({
    list: (since?: string) => listMeetingMessages(meetingId, since),
    post: (body: string) => postMeetingMessage(meetingId, body),
  }), [meetingId])

  const noteIo = useMemo(() => ({
    load: () => getMeetingNote(meetingId),
    save: (body: string) => saveMeetingNote(meetingId, body),
  }), [meetingId])

  return (
    <Panel className="h-[32rem]" bodyClassName="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex flex-wrap gap-1 border-b border-border pb-2">
        {tabs.filter((t) => !t.hidden).map((t) => (
          <button key={t.id} type="button"
            className={`erp-btn h-7 px-2 text-erp-xs ${
              tab === t.id ? 'erp-btn-primary' : 'erp-btn-ghost'}`}
            onClick={() => setTab(t.id)}>
            {t.label}
            {t.badge ? (
              <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-erp-xs text-white">
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'door' && (
          <LobbyPanel meetingId={meetingId} waiting={waiting} inRoom={inRoom}
            onChanged={onChanged} />
        )}
        {tab === 'chat' && <ChatPanel io={chatIo} />}
        {tab === 'notes' && <NotesPanel io={noteIo} />}
        {tab === 'archive' && (
          <ArchivePanel meetingId={meetingId} recordingEnabled={recordingEnabled}
            live={live} canHost={isHost} onToggled={onChanged} />
        )}
        {tab === 'transcript' && <TranscriptPanel meetingId={meetingId} />}
        {tab === 'analysis' && (
          <AnalysisPanel meetingId={meetingId} canHost={isHost} />
        )}
      </div>
    </Panel>
  )
}
/**
 * Issue a join link.
 *
 * The token comes back once and is never retrievable again — only its hash is kept — so it is
 * shown immediately, copied with one press, and the panel says plainly that closing it loses
 * the link. Rotating is cheap; recovering is impossible by design.
 */
function LinkButton({ meetingId }: { meetingId: string }) {
  const [token, setToken] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  const url = token ? `${window.location.origin}/join/${token}` : null

  if (url) {
    return (
      <span className="flex items-center gap-2">
        <input className="erp-input h-9 w-72 text-erp-xs" readOnly value={url}
          onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
          onClick={() => {
            navigator.clipboard?.writeText(url).then(() => setCopied(true)).catch(() => {})
          }}>
          <Copy className="size-4" /> {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm"
          onClick={() => setToken(null)}>
          Done
        </button>
      </span>
    )
  }
  return (
    <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" disabled={busy}
      onClick={() => {
        setBusy(true)
        issueMeetingLink(meetingId, {})
          .then((link) => setToken(link.token))
          .finally(() => setBusy(false))
      }}>
      <Link2 className="size-4" /> {busy ? 'Issuing…' : 'Join link'}
    </button>
  )
}
